import React from "react";
import PropTypes from "prop-types";
import { Modal } from "antd";
import { Icon, ICONS } from "./ui";
import { UPLOAD_DOC_TYPES, UPLOAD_APPLICANT_TYPES, duplicateFileNames } from "./data-utils";

/**
 * Upload files under a chosen document type (and applicant, for applicant
 * level types) — the Document Review upload form, drawn in workspace style.
 * The type list, applicant rule and duplicate-name check are the form's own.
 */
class UploadDocsModal extends React.Component {
    state = { documentType: null, associate: "", files: [], dragging: false, error: null };

    inputRef = React.createRef();

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.setState({ documentType: null, associate: "", files: [], dragging: false, error: null });
        }
    }

    needsApplicant() {
        return UPLOAD_APPLICANT_TYPES.indexOf(this.state.documentType) >= 0;
    }

    addFiles = fileList => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;
        // Same name + size is the same pick twice, not a second file.
        const seen = new Set(this.state.files.map(f => `${f.name}:${f.size}`));
        this.setState({
            files: this.state.files.concat(incoming.filter(f => !seen.has(`${f.name}:${f.size}`))),
            error: null
        });
    };

    removeFile = index =>
        this.setState({ files: this.state.files.filter((_, i) => i !== index) });

    onDrop = e => {
        e.preventDefault();
        this.setState({ dragging: false });
        if (!this.props.disabled) this.addFiles(e.dataTransfer.files);
    };

    submit = () => {
        const { documentType, associate, files } = this.state;
        let error = null;
        if (!documentType) error = "Pick the document type.";
        else if (this.needsApplicant() && !associate) error = "Pick the applicant this document belongs to.";
        else if (!files.length) error = "Add at least one file.";
        else {
            const dupes = duplicateFileNames(files, this.props.existingNames);
            if (dupes.length) error = `Already on the case: ${dupes.join(", ")}. Rename before uploading.`;
        }
        if (error) {
            this.setState({ error });
            return;
        }
        this.props.onSubmit({
            document_type: documentType,
            associate: this.needsApplicant() ? Number(associate) : undefined,
            files
        });
    };

    render() {
        const { open, onClose, applicants, busy, disabled } = this.props;
        const { documentType, associate, files, dragging, error } = this.state;
        return (
            <Modal
                visible={open}
                destroyOnClose
                className={"ops-ws-modal"}
                wrapClassName={"ops-ws-modal-wrap"}
                width={560}
                title={"Upload documents"}
                okText={busy ? "Uploading…" : files.length ? `Upload ${files.length} file${files.length > 1 ? "s" : ""}` : "Upload"}
                okButtonProps={{ disabled: disabled || busy }}
                confirmLoading={busy}
                onOk={this.submit}
                onCancel={() => !busy && onClose()}
            >
                <div className={"ops-ws__field-label"}>{"Document type"}</div>
                <div className={"ops-ws__chip-grid"}>
                    {UPLOAD_DOC_TYPES.map(type => (
                        <button
                            key={type.value}
                            type={"button"}
                            className={"ops-ws__chip" + (documentType === type.value ? " is-active" : "")}
                            disabled={disabled || busy}
                            onClick={() => this.setState({ documentType: type.value, error: null })}
                        >
                            {type.label}
                        </button>
                    ))}
                </div>

                {this.needsApplicant() ? (
                    <div className={"ops-ws__field"} style={{ marginTop: 14 }}>
                        <div className={"ops-ws__field-label"}>{"Applicant"}</div>
                        <select
                            className={"ops-ws__select"}
                            value={associate}
                            disabled={disabled || busy}
                            onChange={e => this.setState({ associate: e.target.value, error: null })}
                        >
                            <option value={""}>{"Select applicant"}</option>
                            {applicants.map(a => (
                                <option key={a.id} value={a.id}>
                                    {a.name}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : null}

                <div className={"ops-ws__field-label"} style={{ marginTop: 14 }}>{"Files"}</div>
                <div
                    className={"ops-ws__drop" + (dragging ? " is-over" : "")}
                    role={"button"}
                    tabIndex={0}
                    onClick={() => !disabled && this.inputRef.current && this.inputRef.current.click()}
                    onKeyDown={e => {
                        if ((e.key === "Enter" || e.key === " ") && this.inputRef.current) this.inputRef.current.click();
                    }}
                    onDragOver={e => {
                        e.preventDefault();
                        if (!dragging) this.setState({ dragging: true });
                    }}
                    onDragLeave={() => this.setState({ dragging: false })}
                    onDrop={this.onDrop}
                >
                    <Icon d={ICONS.download} size={18} />
                    <span className={"ops-ws__drop-title"}>{"Drop files here or click to browse"}</span>
                    <span className={"ops-ws__hint"}>{"PDF or images · several at once is fine"}</span>
                    <input
                        ref={this.inputRef}
                        type={"file"}
                        multiple
                        hidden
                        onChange={e => {
                            this.addFiles(e.target.files);
                            e.target.value = "";
                        }}
                    />
                </div>

                {files.length ? (
                    <div className={"ops-ws__file-list"}>
                        {files.map((file, index) => (
                            <div key={`${file.name}-${index}`} className={"ops-ws__file-row"}>
                                <Icon d={ICONS.file} size={14} />
                                <span className={"ops-ws__file-name"}>{file.name}</span>
                                <span className={"ops-ws__hint"}>{`${Math.max(1, Math.round(file.size / 1024))} KB`}</span>
                                <button
                                    type={"button"}
                                    className={"ops-ws__close ops-ws__rt-remove"}
                                    onClick={() => this.removeFile(index)}
                                    disabled={busy}
                                    aria-label={`Remove ${file.name}`}
                                >
                                    <Icon d={ICONS.cross} size={12} width={2.2} />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : null}

                {error ? <div className={"ops-ws__field-error"} style={{ marginTop: 10 }}>{error}</div> : null}
            </Modal>
        );
    }
}

UploadDocsModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    applicants: PropTypes.array.isRequired,
    existingNames: PropTypes.array.isRequired,
    busy: PropTypes.bool,
    disabled: PropTypes.bool
};

export default UploadDocsModal;
